import { getAggregatedEntityAggregation } from '@adornis/aggregated-entity/api/aggregated-entity.js';
import { AggregatedEntity } from '@adornis/aggregated-entity/entities/aggregated-entity.js';
import { logger } from '@adornis/base/logging.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { BHFilter } from '@adornis/buchungsheld-db/entities/bh-filter.js';
import { mongo } from '@adornis/buchungsheld-db/server/mongo.js';
import { onlyDevAndQaGuard } from '../resolver-guard.js';

interface AggregationResult {
  entityClass: string;
  baseCollectionName: string;
  resultCount: number;
  results: any[];
}

interface AggregatedEntityInfo {
  className: string;
  baseCollectionName: string;
}

// Register query to get all aggregated entities
export const getAggregatedEntities = registerQuery({
  operationName: 'getAggregatedEntities',
  params: [],
  type: () => String,
  middlewares: [onlyDevAndQaGuard],
  resolve: () => async () => {
    const aggregatedEntities: AggregatedEntityInfo[] = [];
    baseqlMetaData.entities.forEach(node => {
      const isAggregatedEntity = (() => {
        let parent = node.parent;
        while (parent) {
          if (parent._class === AggregatedEntity._class) {
            return true;
          }
          parent = parent.parent;
        }
        return false;
      })();

      if (isAggregatedEntity) {
        const entity = node.type() as typeof AggregatedEntity;
        try {
          aggregatedEntities.push({
            className: node._class,
            baseCollectionName: entity.baseCollectionName,
          });
        } catch (e) {
          logger.error({ e }, 'getAggregatedEntities');
        }
      }
    });

    return JSON.stringify(aggregatedEntities);
  },
});

// Register query to get default aggregation for an entity
export const getDefaultAggregation = registerQuery({
  operationName: 'getDefaultAggregation',
  params: [
    { name: 'entityClass', type: () => String },
    { name: 'gqlFields', type: () => String },
  ],
  type: () => String,
  middlewares: [onlyDevAndQaGuard],
  resolve: (entityClass: string, _gqlFields: string) => async () => {
    const node = baseqlMetaData.entities.find(entity => entity._class === entityClass);
    if (!node) {
      throw new Error(`Entity ${entityClass} not found`);
    }

    // Check if it's an aggregated entity
    const isAggregatedEntity = (() => {
      let parent = node.parent;
      while (parent) {
        if (parent._class === AggregatedEntity._class) {
          return true;
        }
        parent = parent.parent;
      }
      return false;
    })();

    if (!isAggregatedEntity) {
      throw new Error(`Entity ${entityClass} is not an AggregatedEntity`);
    }

    const entityConst = node.type() as typeof AggregatedEntity;
    const gqlFields = JSON.parse(_gqlFields);
    const aggregation = getAggregatedEntityAggregation(entityConst, new BHFilter({}), gqlFields);

    return JSON.stringify({
      entityClass,
      baseCollectionName: entityConst.baseCollectionName,
      aggregation,
    });
  },
});

// Register query to run a custom aggregation
export const runAggregation = registerQuery({
  operationName: 'runAggregation',
  params: [
    { name: 'entityClass', type: () => String },
    { name: 'aggregationJson', type: () => String },
    { name: 'limit', type: () => Int },
  ],
  type: () => String,
  middlewares: [onlyDevAndQaGuard],
  resolve: (entityClass: string, aggregationJson: string, limit: number) => async () => {
    let aggregation: any[];
    try {
      aggregation = JSON.parse(aggregationJson);
      // Add limit stage if provided and valid
      if (limit && limit > 0) {
        aggregation.push({ $limit: limit });
      }
    } catch (err) {
      throw new Error('Invalid JSON in aggregation parameter');
    }

    if (!aggregation || !Array.isArray(aggregation)) {
      throw new Error('aggregation array is required');
    }

    // Find the entity
    const node = baseqlMetaData.entities.find(entity => entity._class === entityClass);
    if (!node) {
      throw new Error(`Entity ${entityClass} not found`);
    }

    // Check if it's an aggregated entity
    const isAggregatedEntity = (() => {
      let parent = node.parent;
      while (parent) {
        if (parent._class === AggregatedEntity._class) {
          return true;
        }
        parent = parent.parent;
      }
      return false;
    })();

    if (!isAggregatedEntity) {
      throw new Error(`Entity ${entityClass} is not an AggregatedEntity`);
    }

    const entity = node.type() as typeof AggregatedEntity;

    // Run the aggregation
    const collection = mongo().collection(entity.baseCollectionName);
    const result = await collection.aggregate(aggregation).toArray();

    const response: AggregationResult = {
      entityClass,
      baseCollectionName: entity.baseCollectionName,
      resultCount: result.length,
      results: result,
    };

    return JSON.stringify(response);
  },
});
